import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarCheck, DollarSign, Percent, Package, Download,
  TrendingUp, TrendingDown, Calendar as CalendarIcon, Clock,
  Trophy, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics — Spayzo Merchant" },
      { name: "description", content: "Explore bookings, revenue, occupancy and product rentals." },
    ],
  }),
  component: AnalyticsPage,
});

type Range = "today" | "week" | "month" | "custom";

const rangeLabelKeys: Record<Range, string> = {
  today: "merchantAnalytics.range.today",
  week: "merchantAnalytics.range.week",
  month: "merchantAnalytics.range.month",
  custom: "merchantAnalytics.range.custom",
};

const dataByRange: Record<Range, {
  trend: { d: string; v: number }[];
  bookings: { d: string; v: number }[];
  kpi: { bookings: string; revenue: React.ReactNode; occ: string; rentals: string; period: string };
}> = {
  today: {
    trend: [
      { d: "6a", v: 120 }, { d: "9a", v: 340 }, { d: "12p", v: 520 },
      { d: "3p", v: 780 }, { d: "6p", v: 940 }, { d: "9p", v: 620 },
    ],
    bookings: [
      { d: "6a", v: 2 }, { d: "9a", v: 5 }, { d: "12p", v: 8 },
      { d: "3p", v: 11 }, { d: "6p", v: 14 }, { d: "9p", v: 6 },
    ],
    kpi: { bookings: "24", revenue: <Money value="3,320" />, occ: "82.1%", rentals: "38", period: "merchantAnalytics.period.vsYesterday" },
  },
  week: {
    trend: [
      { d: "Mon", v: 1420 }, { d: "Tue", v: 1820 }, { d: "Wed", v: 1640 },
      { d: "Thu", v: 2210 }, { d: "Fri", v: 2840 }, { d: "Sat", v: 3120 },
      { d: "Sun", v: 2480 },
    ],
    bookings: [
      { d: "Mon", v: 12 }, { d: "Tue", v: 18 }, { d: "Wed", v: 14 },
      { d: "Thu", v: 22 }, { d: "Fri", v: 28 }, { d: "Sat", v: 34 },
      { d: "Sun", v: 24 },
    ],
    kpi: { bookings: "152", revenue: <Money value="15,530" />, occ: "78.4%", rentals: "248", period: "merchantAnalytics.period.vsLastWeek" },
  },
  month: {
    trend: [
      { d: "W1", v: 8400 }, { d: "W2", v: 9820 }, { d: "W3", v: 11240 }, { d: "W4", v: 12680 },
    ],
    bookings: [
      { d: "W1", v: 96 }, { d: "W2", v: 118 }, { d: "W3", v: 142 }, { d: "W4", v: 168 },
    ],
    kpi: { bookings: "524", revenue: <Money value="62,140" />, occ: "76.8%", rentals: "912", period: "merchantAnalytics.period.vsLastMonth" },
  },
  custom: {
    trend: [
      { d: "D1", v: 1220 }, { d: "D2", v: 1540 }, { d: "D3", v: 1820 },
      { d: "D4", v: 1980 }, { d: "D5", v: 2340 }, { d: "D6", v: 2620 }, { d: "D7", v: 2810 },
    ],
    bookings: [
      { d: "D1", v: 10 }, { d: "D2", v: 14 }, { d: "D3", v: 16 },
      { d: "D4", v: 20 }, { d: "D5", v: 24 }, { d: "D6", v: 28 }, { d: "D7", v: 30 },
    ],
    kpi: { bookings: "142", revenue: <Money value="14,330" />, occ: "75.6%", rentals: "218", period: "merchantAnalytics.period.vsPriorRange" },
  },
};

const breakdown = [
  { nameKey: "merchantAnalytics.category.studios", share: 38, color: "#5958F3", cls: "bg-[#5958F3]" },
  { nameKey: "merchantAnalytics.category.meetingRooms", share: 26, color: "#10b981", cls: "bg-emerald-500" },
  { nameKey: "merchantAnalytics.category.eventSpaces", share: 18, color: "#f59e0b", cls: "bg-amber-500" },
  { nameKey: "merchantAnalytics.category.podcastBooths", share: 12, color: "#0ea5e9", cls: "bg-sky-500" },
  { nameKey: "merchantAnalytics.category.other", share: 6, color: "#8b5cf6", cls: "bg-violet-500" },
];

const peakHours = [
  { label: "9–11 AM", v: 24 }, { label: "11 AM–1 PM", v: 38 },
  { label: "1–3 PM", v: 62 }, { label: "3–5 PM", v: 78 },
  { label: "5–7 PM", v: 92 }, { label: "7–9 PM", v: 54 },
]; // hour ranges kept as-is (time format)

function Kpi({ icon: Icon, label, value, sub, trend: t, tone = "default" }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value: React.ReactNode; sub?: string;
  trend?: { v: string; up: boolean }; tone?: "default" | "primary";
}) {
  return (
    <Card className="group relative overflow-hidden rounded-2xl border-border/60 p-5 shadow-soft transition-all hover:shadow-md hover:-translate-y-0.5">
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-[#5958F3]/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="relative flex items-start justify-between">
        <span className={cn("grid h-10 w-10 place-items-center rounded-xl ring-1 ring-inset",
          tone === "primary"
            ? "bg-[#5958F3] text-white ring-[#5958F3]/30"
            : "bg-[#5958F3]/10 text-[#5958F3] ring-[#5958F3]/15")}>
          <Icon className="h-5 w-5" />
        </span>
        {t && (
          <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
            t.up ? "bg-emerald-500/10 text-emerald-700" : "bg-rose-500/10 text-rose-700")}>
            {t.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} {t.v}
          </span>
        )}
      </div>
      <p className="relative mt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="relative mt-1 font-display text-3xl font-bold tracking-tight">{value}</p>
      {sub && <p className="relative mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

function AnalyticsPage() {
  const { t } = useTranslation();
  const rangeLabel: Record<Range, string> = {
    today: t("merchantAnalytics.range.today"),
    week: t("merchantAnalytics.range.week"),
    month: t("merchantAnalytics.range.month"),
    custom: t("merchantAnalytics.range.custom"),
  };
  const [range, setRange] = useState<Range>("week");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);

  const data = dataByRange[range];
  const trendMax = Math.max(...data.trend.map((t) => t.v));
  const bookingMax = Math.max(...data.bookings.map((b) => b.v));

  const customLabel =
    customRange?.from && customRange?.to
      ? `${format(customRange.from, "MMM d")} – ${format(customRange.to, "MMM d, yyyy")}`
      : customRange?.from
      ? `${format(customRange.from, "MMM d, yyyy")} – …`
      : t("merchantAnalytics.pickDateRange");

  const handleExportPdf = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(imgData, "PNG", (pageWidth - w) / 2, 24, w, h);
      pdf.save(`analytics-${range}-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(t("merchantAnalytics.toast.exported", { range: rangeLabel[range] }));
    } catch (e) {
      console.error(e);
      toast.error(t("merchantAnalytics.toast.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <MerchantShell
      title={t("merchantAnalytics.title")}
      subtitle={t("merchantAnalytics.subtitle")}
      actions={
        <Button
          onClick={handleExportPdf}
          disabled={exporting}
          className="h-10 rounded-xl shadow-soft"
        >
          <Download className="h-4 w-4" /> {exporting ? t("merchantAnalytics.exporting") : t("merchantAnalytics.exportPdf")}
        </Button>
      }
    >
      {/* Range filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={range} onValueChange={(v) => setRange(v as Range)}>
            <TabsList className="rounded-xl">
              <TabsTrigger value="today" className="rounded-lg">{t("merchantAnalytics.range.today")}</TabsTrigger>
              <TabsTrigger value="week" className="rounded-lg">{t("merchantAnalytics.range.week")}</TabsTrigger>
              <TabsTrigger value="month" className="rounded-lg">{t("merchantAnalytics.range.month")}</TabsTrigger>
              <TabsTrigger value="custom" className="rounded-lg">
                <CalendarIcon className="h-3.5 w-3.5 me-2" /> {t("merchantAnalytics.range.custom")}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {range === "custom" && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "h-9 rounded-xl border-border/60 text-xs font-medium",
                    !customRange?.from && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {customLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={customRange}
                  onSelect={setCustomRange}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("merchantAnalytics.showingPrefix")} <span className="font-semibold text-foreground">
            {range === "custom" && customRange?.from ? customLabel : rangeLabel[range]}
          </span> {t("merchantAnalytics.showingSuffix")}
        </p>
      </div>

      <div ref={captureRef} className="bg-background">
        {/* KPI metrics */}
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi icon={CalendarCheck} label={t("merchantAnalytics.kpi.bookings")} value={data.kpi.bookings} sub={t(data.kpi.period)}
            trend={{ v: "+13.4%", up: true }} tone="primary" />
          <Kpi icon={DollarSign} label={t("merchantAnalytics.kpi.revenue")} value={data.kpi.revenue} sub={t(data.kpi.period)}
            trend={{ v: "+17.6%", up: true }} />
          <Kpi icon={Percent} label={t("merchantAnalytics.kpi.occupancy")} value={data.kpi.occ} sub={t(data.kpi.period)}
            trend={{ v: "+7.2pt", up: true }} />
          <Kpi icon={Package} label={t("merchantAnalytics.kpi.rentals")} value={data.kpi.rentals} sub={t(data.kpi.period)}
            trend={{ v: "-7.5%", up: false }} />
        </div>

        {/* Line + Bar charts */}
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Line chart */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("merchantAnalytics.revenueTrend")}</p>
                <p className="mt-1 font-display text-xl font-bold tracking-tight">{data.kpi.revenue}</p>
              </div>
              <Badge className="rounded-full bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10">
                <ArrowUpRight className="h-3 w-3" /> 17.6%
              </Badge>
            </div>
            <div className="mt-5">
              <svg viewBox="0 0 600 200" className="h-52 w-full" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="a-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#5958F3" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#5958F3" stopOpacity="0" />
                  </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => (
                  <line key={i} x1="0" x2="600" y1={40 + i * 40} y2={40 + i * 40}
                    stroke="hsl(var(--border))" strokeDasharray="3 4" />
                ))}
                {(() => {
                  const pts = data.trend.map((t, i) => {
                    const x = (i / (data.trend.length - 1)) * 600;
                    const y = 190 - (t.v / trendMax) * 160;
                    return [x, y] as const;
                  });
                  const path = pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
                  const area = `${path} L600,200 L0,200 Z`;
                  return (
                    <>
                      <path d={area} fill="url(#a-grad)" />
                      <path d={path} fill="none" stroke="#5958F3" strokeWidth="2.5" strokeLinejoin="round" />
                      {pts.map(([x, y], i) => (
                        <circle key={i} cx={x} cy={y} r="3.5" fill="#ffffff" stroke="#5958F3" strokeWidth="2" />
                      ))}
                    </>
                  );
                })()}
              </svg>
              <div className="mt-2 flex justify-between text-[10px] font-medium text-muted-foreground">
                {data.trend.map((t) => <span key={t.d}>{t.d}</span>)}
              </div>
            </div>
          </Card>

          {/* Bar chart */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("merchantAnalytics.bookingActivity")}</p>
                <p className="mt-1 font-display text-xl font-bold tracking-tight">{t("merchantAnalytics.bookingsCount", { count: data.kpi.bookings })}</p>
              </div>
              <Badge className="rounded-full bg-[#5958F3]/10 text-[#5958F3] hover:bg-[#5958F3]/10">{rangeLabel[range]}</Badge>
            </div>
            <div className="mt-5 flex h-52 items-end gap-3">
              {data.bookings.map((b) => {
                const h = (b.v / bookingMax) * 100;
                return (
                  <div key={b.d} className="flex flex-1 flex-col items-center gap-2">
                    <div className="relative flex h-full w-full items-end">
                      <div className="w-full rounded-t-lg bg-gradient-to-t from-[#5958F3] to-[#8483ff] transition-all"
                        style={{ height: `${h}%` }}>
                        <span className="absolute -top-5 start-1/2 -translate-x-1/2 text-[10px] font-semibold text-muted-foreground">
                          {b.v}
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] font-medium text-muted-foreground">{b.d}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Pie + insights */}
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Pie */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("merchantAnalytics.bookingsByCategory")}</p>
            <p className="mt-1 font-display text-xl font-bold tracking-tight">{t("merchantAnalytics.mix")}</p>
            <div className="mt-4 flex flex-col items-center gap-4">
              <div className="relative h-40 w-40 shrink-0">
                <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                  {(() => {
                    let acc = 0;
                    return breakdown.map((b) => {
                      const dash = b.share;
                      const gap = 100 - dash;
                      const off = -acc;
                      acc += dash;
                      return (
                        <circle key={b.nameKey} cx="18" cy="18" r="15.9155" fill="transparent"
                          stroke={b.color} strokeWidth="5" strokeDasharray={`${dash} ${gap}`} strokeDashoffset={off} />
                      );
                    });
                  })()}
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <p className="text-[10px] font-medium uppercase text-muted-foreground">{t("merchantAnalytics.total")}</p>
                    <p className="font-display text-xl font-bold">{data.kpi.bookings}</p>
                  </div>
                </div>
              </div>
              <div className="w-full space-y-2">
                {breakdown.map((b) => (
                  <div key={b.nameKey} className="flex items-center gap-2 text-xs">
                    <span className={cn("h-2 w-2 rounded-full", b.cls)} />
                    <span className="flex-1 truncate">{t(b.nameKey)}</span>
                    <span className="font-semibold tabular-nums">{b.share}%</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Top booked space */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft bg-gradient-to-br from-[#5958F3]/8 via-background to-background">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#5958F3] text-white">
                <Trophy className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-base font-semibold">{t("merchantAnalytics.topBookedSpace")}</p>
                <p className="text-xs text-muted-foreground">{rangeLabel[range]}</p>
              </div>
            </div>
            <div className="mt-5">
              <div className="aspect-[16/9] w-full rounded-xl bg-gradient-to-br from-[#5958F3]/30 via-[#5958F3]/15 to-background ring-1 ring-border/60" />
              <p className="mt-3 font-display text-lg font-bold tracking-tight">{t("merchantAnalytics.loftStudio")}</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-display text-2xl font-bold">42</span>
                <span className="text-xs text-muted-foreground">{t("merchantAnalytics.bookingsLower")}</span>
                <Badge className="ms-auto rounded-full bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/10">
                  <TrendingUp className="h-3 w-3" /> +24%
                </Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-background p-2.5">
                  <p className="text-muted-foreground">{t("merchantAnalytics.revenueLabel")}</p>
                  <p className="font-semibold"><Riyal /> 5,820</p>
                </div>
                <div className="rounded-lg bg-background p-2.5">
                  <p className="text-muted-foreground">{t("merchantAnalytics.occupancyLabel")}</p>
                  <p className="font-semibold">92%</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Peak booking times */}
          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-amber-500/15 text-amber-600">
                <Clock className="h-4 w-4" />
              </span>
              <div>
                <p className="font-display text-base font-semibold">{t("merchantAnalytics.peakBookingTimes")}</p>
                <p className="text-xs text-muted-foreground">{t("merchantAnalytics.demandByHour")}</p>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {peakHours.map((h) => (
                <div key={h.label}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium">{h.label}</span>
                    <span className="font-semibold tabular-nums">{h.v}%</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-surface">
                    <div className={cn("h-full rounded-full",
                      h.v >= 80 ? "bg-[#5958F3]" : h.v >= 50 ? "bg-amber-500" : "bg-muted-foreground/40")}
                      style={{ width: `${h.v}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-xl bg-surface p-3 text-xs">
              <p className="font-semibold">{t("merchantAnalytics.insight")}</p>
              <p className="mt-1 text-muted-foreground">
                {t("merchantAnalytics.insightTextBefore")} <span className="font-semibold text-foreground">5–7 PM</span>. {t("merchantAnalytics.insightTextAfter")}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </MerchantShell>
  );
}
